import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, ArrowLeftRight, BarChart3 } from "lucide-react";
import type { AnyRow } from "@/lib/rowTypes";

interface VocabStat {
  course_name: string;
  total_terms: number;
  priority_1: number;
  priority_2: number;
  priority_3: number;
  with_expanded: number;
  with_age_band: number;
}

interface ContrastPairRow {
  id: string;
  term_a: string;
  term_b: string;
  course_name: string | null;
  priority: number;
  term_family: string | null;
}

export default function VocabAdminPanel() {
  const [stats, setStats] = useState<VocabStat[]>([]);
  const [pairs, setPairs] = useState<ContrastPairRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Get vocab terms grouped by course
      const { data: vocab } = await supabase
        .from("lesson_vocabulary")
        .select("term, definition, priority, definition_expanded, age_band_definition_11_13, lesson_id, lessons!inner(course_name)")
        .order("sort_order");

      const courseMap = new Map<string, VocabStat>();
      ((vocab) || []).forEach((v: AnyRow) => {
        const cn = v.lessons?.course_name || "Unknown";
        if (!courseMap.has(cn)) {
          courseMap.set(cn, { course_name: cn, total_terms: 0, priority_1: 0, priority_2: 0, priority_3: 0, with_expanded: 0, with_age_band: 0 });
        }
        const s = courseMap.get(cn)!;
        s.total_terms++;
        if (v.priority === 1) s.priority_1++;
        else if (v.priority === 3) s.priority_3++;
        else s.priority_2++;
        if (v.definition_expanded) s.with_expanded++;
        if (v.age_band_definition_11_13) s.with_age_band++;
      });
      setStats(Array.from(courseMap.values()).sort((a, b) => a.course_name.localeCompare(b.course_name)));

      // Get contrast pairs
      const { data: cp } = await supabase.from("vocab_contrast_pairs").select("id, term_a, term_b, course_name, priority, term_family").order("sort_order");
      setPairs((cp) || []);

      setLoading(false);
    };
    void load();
  }, []);

  if (loading) {
    return <div className="text-sm text-muted-foreground p-4">Loading vocabulary data…</div>;
  }

  const totalTerms = stats.reduce((s, c) => s + c.total_terms, 0);
  const totalP1 = stats.reduce((s, c) => s + c.priority_1, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Total Terms", value: totalTerms },
          { label: "Core Mastery (P1)", value: totalP1 },
          { label: "Contrast Pairs", value: pairs.length },
          { label: "Courses Covered", value: stats.length },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border/60 bg-card/60 p-3 text-center">
            <p className="font-heading text-lg font-bold text-foreground">{s.value}</p>
            <p className="text-[10px] text-muted-foreground font-heading uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="library">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="library" className="text-xs gap-1"><BookOpen className="h-3 w-3" /> Library</TabsTrigger>
          <TabsTrigger value="contrasts" className="text-xs gap-1"><ArrowLeftRight className="h-3 w-3" /> Contrasts</TabsTrigger>
          <TabsTrigger value="coverage" className="text-xs gap-1"><BarChart3 className="h-3 w-3" /> Coverage</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="space-y-3 mt-3">
          {stats.map((s) => (
            <div key={s.course_name} className="rounded-xl border border-border/60 bg-card/60 p-3">
              <p className="font-heading text-xs font-semibold text-foreground truncate">{s.course_name}</p>
              <div className="flex gap-4 mt-1.5 text-[10px] text-muted-foreground font-heading">
                <span>{s.total_terms} terms</span>
                <span className="text-primary">P1: {s.priority_1}</span>
                <span>P2: {s.priority_2}</span>
                <span className="text-muted-foreground/60">P3: {s.priority_3}</span>
              </div>
              <div className="flex gap-4 mt-1 text-[10px] text-muted-foreground">
                <span>Expanded: {s.with_expanded}/{s.total_terms}</span>
                <span>Age-band: {s.with_age_band}/{s.total_terms}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="contrasts" className="space-y-2 mt-3">
          {pairs.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/60 p-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-heading font-bold text-primary truncate">{p.term_a}</span>
                <span className="text-[10px] text-muted-foreground">vs</span>
                <span className="rounded-md bg-accent/10 px-2 py-0.5 text-xs font-heading font-bold text-accent truncate">{p.term_b}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.term_family && <span className="text-[9px] text-muted-foreground/60 font-heading">{p.term_family}</span>}
                <span className={`text-[9px] font-heading font-bold ${p.priority === 1 ? "text-primary" : "text-muted-foreground"}`}>P{p.priority}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="coverage" className="space-y-3 mt-3">
          {stats.map((s) => {
            const expandedPct = s.total_terms ? Math.round((s.with_expanded / s.total_terms) * 100) : 0;
            const agePct = s.total_terms ? Math.round((s.with_age_band / s.total_terms) * 100) : 0;
            return (
              <div key={s.course_name} className="rounded-xl border border-border/60 bg-card/60 p-3">
                <p className="font-heading text-xs font-semibold text-foreground truncate mb-2">{s.course_name}</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-20 shrink-0">Expanded</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${expandedPct}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-8 text-right">{expandedPct}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-20 shrink-0">Age-band</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${agePct}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-8 text-right">{agePct}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
